import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import s from './HeroScroll.module.scss';
import Icon from '../Icon/Icon';
import classNames from 'classnames';

const renderTextWithHighlights = (input) => {
    return input.split(/(\s+)/).map((chunk, index) => {
        if (chunk.startsWith('#')) {
            return (
                <span key={index} className={s.Highlight}>
                    {chunk.slice(1)}
                </span>
            );
        }
        return <span key={index}>{chunk}</span>;
    });
};

const HeroScroll = ({ title = '', text = '', video = '' }) => {
    const [expanded, setExpanded] = useState(false);

    const textRef = useRef(null);
    const isInView = useInView(textRef, { once: true, margin: '-100px 0px' });

    useEffect(() => {
        const timeout = setTimeout(() => {
            setExpanded(true);
        }, 1500);
        return () => clearTimeout(timeout);
    }, []);

    const isAbsoluteUrl = (url) => /^https?:\/\//.test(url);
    const mediaUrl = process.env.NEXT_PUBLIC_MEDIA_URL;
    const resolvedVideoSrc = isAbsoluteUrl(video)
        ? video
        : `${mediaUrl}${video}`;

    return (
        <div className={s.Root}>
            <div className={s.CenterWrapper}>
                <div className={s.ImageWrapper}>
                    <motion.div
                        className={s.LogoWrapper}
                        initial={{ opacity: 1 }}
                        animate={{ opacity: expanded ? 0 : 1 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}>
                        <Icon name="LogoLarge" />
                    </motion.div>

                    <motion.div
                        className={classNames(s.ClipContainer, {
                            [s['Expanded']]: expanded,
                        })}
                        animate={expanded ? 'expanded' : 'initial'}
                        initial="initial"
                        variants={{
                            initial: {
                                width: 170,
                                height: 170,
                                transition: { duration: 0.6, ease: 'easeOut' },
                            },
                            expanded: {
                                width: '100%',
                                height: '100%',
                                transition: { duration: 0.5, ease: 'easeOut' },
                            },
                        }}>
                        <video
                            className={s.Video}
                            src={resolvedVideoSrc}
                            autoPlay
                            loop
                            muted
                            playsInline
                        />
                    </motion.div>
                </div>

                <motion.div
                    className={s.TextWrapper}
                    variants={{
                        initial: { opacity: 0, y: 200 },
                        expanded: {
                            opacity: 1,
                            y: 0,
                            transition: {
                                duration: 0.6,
                                ease: 'easeOut',
                            },
                        },
                    }}
                    initial="initial"
                    animate={expanded ? 'expanded' : 'initial'}>
                    <h1 className={s.Title}>
                        {renderTextWithHighlights(title)}
                    </h1>

                    {text && (
                        <motion.p
                            ref={textRef}
                            className={s.Text}
                            initial={{ opacity: 0 }}
                            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}>
                            {text}
                        </motion.p>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default HeroScroll;
